import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow, { tableRowClasses, TableRowProps } from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import { t } from 'i18next';
import {
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  ComponentType,
  CSSProperties,
  forwardRef,
  Fragment,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ListItem, TableComponents, TableVirtuoso } from 'react-virtuoso';

import DataGridBottomBar from '~components/DataGrid/DataGridBottomBar';
import DataTableCellContent from '~components/DataTable/DataTableCellContent';
import { DataTableColumn, DataTableDensity } from '~components/DataTable/dataTableColumn';
import DataTableColumnChooser from '~components/DataTable/DataTableColumnChooser';
import { columnWidthVariableName } from '~components/DataTable/dataTableColumnVariables';
import DataTableSearchField from '~components/DataTable/DataTableSearchField';
import { subscribeToSharedLayoutResets } from '~components/DataTable/dataTableSharedLayout';
import getColumnSlots, {
  CELL_HORIZONTAL_PADDING_PX,
} from '~components/DataTable/getColumnSlots';
import getDataTableTokens from '~components/DataTable/getDataTableTokens';
import useColumnFloors from '~components/DataTable/useColumnFloors';
import useColumnResize from '~components/DataTable/useColumnResize';
import useDataTableColumnLayout from '~components/DataTable/useDataTableColumnLayout';
import usePinnedOffsets from '~components/DataTable/usePinnedOffsets';
import { NoDataFound } from '~components/NoDataFound/NoDataFound';

// Standard matches MUI DataGrid's default header and row heights, so migrated pages keep
// their density. Compact fits more rows on dense screens.
const DENSITY_SIZES = {
  compact: {
    chipHeightPx: 20,
    fontSize: '11px',
    headerHeightPx: 40,
    rowHeightPx: 34,
  },
  standard: {
    chipHeightPx: undefined,
    fontSize: '12px',
    headerHeightPx: 56,
    rowHeightPx: 52,
  },
};
// The no-rows area height the previous MUI X grid used.
const NO_ROWS_AREA_HEIGHT_PX = 104;
// The detail-panel toggle and checkbox column widths the previous MUI X grid used.
const DETAIL_TOGGLE_COLUMN_WIDTH_PX = 40;
const SELECTION_COLUMN_WIDTH_PX = 42;

const RESIZE_HANDLE_WIDTH_PX = 8;
// The height of the separator icon MUI DataGrid draws between column headers.
const SEPARATOR_HEIGHT_PX = 24;
const LOADING_BODY_OPACITY = 0.6;
// Rows rendered past each edge of a virtualized table, so fast scrolls show no gaps.
const OVERSCAN_ROW_COUNT = 10;
const VISIBLE_WIDTH_VARIABLE = '--data-table-visible-width';

export const STICKY_CELL_Z_INDEX = 1;

// Full-width rows stick to the visible width, so they stay on screen while the columns
// scroll sideways.
const fullWidthContentSx = {
  left: 0,
  overflowX: 'auto',
  position: 'sticky',
  width: `var(${VISIBLE_WIDTH_VARIABLE})`,
} as const;

interface DataTableRow {
  id: string;
}

interface DataTableRowColors {
  // Painted on the row's leading expand or select cell, and on its detail panel.
  accent?: string;
  // Replaces the row's and its detail panel's background, and turns off the hover.
  surface?: string;
  // A translucent color laid over the row's surface, pinned cells included.
  tint?: string;
}

interface DataTableProps<TRow extends DataTableRow> {
  columns: Array<DataTableColumn<TRow>>;
  // Moves the column chooser out of the toolbar, behind a 3-dot button rendered here.
  columnsMenuContainer?: HTMLElement | null;
  density?: DataTableDensity;
  expandedRowIds?: string[];
  footer?: ReactNode;
  getRowColors?: (row: TRow, isExpanded: boolean) => DataTableRowColors | undefined;
  headerLeadingComponent?: ReactNode;
  headerTrailingComponent?: ReactNode;
  hideToolbar?: boolean;
  id: string;
  // Renders only the rows in view, inside a scroller that fills the table's height.
  isVirtualized?: boolean;
  loading?: boolean;
  noRowsOverlayAction?: ReactNode;
  noRowsOverlayText?: string;
  onEndReached?: () => void;
  onExpandedRowIdsChange?: (expandedRowIds: string[]) => void;
  onRowClick?: (row: TRow) => void;
  onSearchChange?: (searchValue: string) => void;
  onSelectedRowIdsChange?: (selectedRowIds: string[]) => void;
  renderDetailPanel?: (row: TRow) => ReactNode;
  // Renders each body row, for rows that need hooks such as a drop target. It must render
  // a TableRow with the props it is given.
  rowComponent?: ComponentType<TableRowProps & { row: TRow }>;
  rows: TRow[];
  selectedRowIds?: string[];
  surfaceColor?: string;
  sx?: SxProps<Theme>;
}

type DataTableTokens = ReturnType<typeof getDataTableTokens>;

type BodyRowProps = TableRowProps & Record<'data-id' | 'data-test-id', string>;

type VirtualizedItem<TRow> = { kind: 'detail' | 'row'; row: TRow } | { kind: 'footer' };

interface VirtualizedTableContext {
  bodySx: SxProps<Theme>;
  renderRow: (index: number, props: TableRowProps) => ReactNode;
  tableProps: ComponentPropsWithRef<typeof Table>;
}

type VirtualizedSectionProps = Pick<
  ComponentPropsWithoutRef<'tbody'>,
  'children' | 'className' | 'style'
> & { context: VirtualizedTableContext };

// Module-level so their identity holds; TableVirtuoso remounts the table when it changes.
const virtualizedTableComponents: TableComponents<unknown, VirtualizedTableContext> = {
  Table: ({ children, context, style }) => (
    <Table {...context.tableProps} style={{ ...style, ...context.tableProps.style }}>
      {children}
    </Table>
  ),
  TableBody: forwardRef<HTMLTableSectionElement, VirtualizedSectionProps>(
    function VirtualizedTableBody({ context, ...props }, ref) {
      return <TableBody {...props} ref={ref} sx={context.bodySx} />;
    },
  ),
  TableHead: forwardRef<HTMLTableSectionElement, VirtualizedSectionProps>(
    function VirtualizedTableHead({ children, style }, ref) {
      return (
        <TableHead ref={ref} style={style}>
          {children}
        </TableHead>
      );
    },
  ),
  TableRow: ({ context, ...props }) => context.renderRow(props['data-index'], props),
};

type ColumnSlot<TRow> = ReturnType<typeof getColumnSlots> & {
  column: DataTableColumn<TRow>;
};

// Lays custom cell content out in a row, as the previous MUI X grid's `display: 'flex'` cells did.
// Without it a block-level child (a menu, a Box) drops onto its own line.
const customCellContentSx: SxProps<Theme> = {
  alignItems: 'center',
  display: 'inline-flex',
  // Lets a child that truncates (minWidth: 0 plus an ellipsis) shrink to the cell.
  maxWidth: '100%',
  verticalAlign: 'middle',
};

const cellPaddingSx = {
  px: `${CELL_HORIZONTAL_PADDING_PX}px`,
  py: 0,
};

const getAccentShadow = (accentColor?: string) =>
  accentColor ? `inset 3px 0 0 ${accentColor}` : undefined;

interface CellBackground {
  backgroundColor: string;
  backgroundImage?: string;
  // An opaque sticky cell hides the row's hover color, so it lays the hover over itself.
  hoverBackgroundImage?: string;
}

const stickyCellSx = ({ hoverBackgroundImage, ...background }: CellBackground) => ({
  ...background,
  position: 'sticky' as const,
  zIndex: STICKY_CELL_Z_INDEX,
  [`.${tableRowClasses.hover}:hover > &`]: { backgroundImage: hoverBackgroundImage },
});

interface PinnedEdges {
  borderColor: string;
  firstEndField?: string;
  lastStartField?: string;
}

type PinnableColumn = Pick<DataTableColumn<never>, 'field' | 'pin'>;

// The pinned column next to the scrolling ones draws a border on that side.
function getPinnedSx(
  column: PinnableColumn,
  background: CellBackground,
  edges: PinnedEdges,
) {
  if (column.pin === 'left') {
    return {
      ...stickyCellSx(background),
      ...(edges.lastStartField === column.field
        ? { borderRight: 1, borderRightColor: edges.borderColor }
        : {}),
    };
  }

  if (column.pin === 'right') {
    return {
      ...stickyCellSx(background),
      ...(edges.firstEndField === column.field
        ? { borderLeft: 1, borderLeftColor: edges.borderColor }
        : {}),
    };
  }

  return {};
}

function getPinnedOffsetStyle(
  column: PinnableColumn,
  pinnedOffsets: Record<string, number>,
  leadingColumnsWidth: number,
): CSSProperties {
  if (column.pin === 'left') {
    return { left: pinnedOffsets[column.field] ?? leadingColumnsWidth };
  }

  if (column.pin === 'right') {
    return { right: pinnedOffsets[column.field] ?? 0 };
  }

  return {};
}

const separatorLineSx = (tokens: DataTableTokens): SxProps<Theme> => ({
  '&::after': {
    backgroundColor: tokens.separatorColor,
    content: '""',
    height: SEPARATOR_HEIGHT_PX,
    position: 'absolute',
    right: 0,
    top: `calc(50% - ${SEPARATOR_HEIGHT_PX / 2}px)`,
    width: '1px',
  },
});

// Expanding or selecting a row shouldn't also count as clicking it.
const stopRowClick = (event: MouseEvent) => event.stopPropagation();

function getCellContent<TRow>(column: DataTableColumn<TRow>, row: TRow) {
  if (column.renderCell) {
    return { content: column.renderCell(row), plainText: undefined };
  }

  const value = column.valueGetter?.(row);

  return { content: value, plainText: typeof value === 'string' ? value : undefined };
}

function DataTableContent<TRow extends DataTableRow>({
  columns,
  columnsMenuContainer,
  density = 'standard',
  expandedRowIds: controlledExpandedRowIds,
  footer,
  getRowColors,
  headerLeadingComponent,
  headerTrailingComponent,
  hideToolbar = false,
  id,
  isVirtualized = false,
  loading = false,
  noRowsOverlayAction,
  noRowsOverlayText,
  onEndReached,
  onExpandedRowIdsChange,
  onRowClick,
  onSearchChange,
  onSelectedRowIdsChange,
  renderDetailPanel,
  rowComponent: RowComponent,
  rows: incomingRows,
  selectedRowIds,
  surfaceColor,
  sx,
}: DataTableProps<TRow>) {
  // Pages often hand over no rows while the next page loads; keeping the last loaded
  // rows under the progress bar stops the table blanking on every page change.
  const lastLoadedRowsRef = useRef(incomingRows);
  if (!loading) {
    lastLoadedRowsRef.current = incomingRows;
  }
  const rows = lastLoadedRowsRef.current;

  const theme = useTheme();
  const tableRef = useRef<HTMLTableElement>(null);

  // React 18 has no boolean `inert` prop, so it's set on the DOM node. The kept rows
  // are the previous page's, and their buttons must not act while the next page loads.
  useLayoutEffect(() => {
    const tableBody = tableRef.current?.tBodies[0];

    if (tableBody) {
      tableBody.inert = loading;
    }
  }, [loading]);
  const tokens = useMemo(
    () => getDataTableTokens(theme, surfaceColor),
    [surfaceColor, theme],
  );
  const { chipHeightPx, fontSize, headerHeightPx, rowHeightPx } = DENSITY_SIZES[density];
  const [uncontrolledExpandedRowIds, setUncontrolledExpandedRowIds] = useState<string[]>(
    [],
  );
  const expandedRowIds = controlledExpandedRowIds ?? uncontrolledExpandedRowIds;
  const expandedRowIdSet = useMemo(() => new Set(expandedRowIds), [expandedRowIds]);

  const hasDetailPanel = Boolean(renderDetailPanel);
  const hasRowSelection = Boolean(selectedRowIds);
  const leadingColumnsWidth =
    (hasDetailPanel ? DETAIL_TOGGLE_COLUMN_WIDTH_PX : 0) +
    (hasRowSelection ? SELECTION_COLUMN_WIDTH_PX : 0);

  const {
    chooserColumns,
    columnOrder,
    columnsByField,
    columnVisibility,
    columnWidths,
    moveColumn,
    pinnedColumns,
    reset,
    setColumnWidth,
    toggleColumn,
  } = useDataTableColumnLayout(id, columns);

  const draggedWidthOf = useCallback(
    (column: DataTableColumn<TRow>) =>
      getColumnSlots(column).isResizable ? columnWidths[column.field] : undefined,
    [columnWidths],
  );

  const visibleColumnSlots = useMemo(() => {
    const isVisible = (column: DataTableColumn<TRow>) =>
      columnVisibility[column.field] !== false;
    const unpinnedInOrder = columnOrder.flatMap((field) => {
      const column = columnsByField.get(field);

      return column && !column.pin ? [column] : [];
    });
    const toSlots = (slotColumns: Array<DataTableColumn<TRow>>) =>
      slotColumns.filter(isVisible).map((column): ColumnSlot<TRow> => ({
        column,
        ...getColumnSlots(column),
      }));

    return {
      center: toSlots(unpinnedInOrder),
      end: toSlots(pinnedColumns.right),
      start: toSlots(pinnedColumns.left),
    };
  }, [columnOrder, columnVisibility, columnsByField, pinnedColumns]);

  const draggedWidthVariables = useMemo(
    () =>
      Object.fromEntries(
        columns.flatMap((column) => {
          const draggedWidth = draggedWidthOf(column);

          return draggedWidth === undefined
            ? []
            : [[columnWidthVariableName(column.field), `${draggedWidth}px`]];
        }),
      ) as CSSProperties,
    [columns, draggedWidthOf],
  );

  const pinnedFields = useMemo(
    () => ({
      end: visibleColumnSlots.end.map((slot) => slot.column.field),
      start: visibleColumnSlots.start.map((slot) => slot.column.field),
    }),
    [visibleColumnSlots],
  );

  const pinnedOffsets = usePinnedOffsets({
    endFields: pinnedFields.end,
    leadingColumnsWidth,
    startFields: pinnedFields.start,
    tableRef,
  });

  const fittedMaxWidths = useMemo(
    () =>
      Object.fromEntries(
        columns.map((column) => [column.field, getColumnSlots(column).fittedMaxWidth]),
      ),
    [columns],
  );
  const visibleFieldsKey = [
    ...visibleColumnSlots.start,
    ...visibleColumnSlots.center,
    ...visibleColumnSlots.end,
  ]
    .map((slot) => slot.column.field)
    .join(',');
  const { growFloors, refitColumn, resetFloors } = useColumnFloors(
    tableRef,
    rows,
    visibleFieldsKey,
    fittedMaxWidths,
    density,
  );

  const { startResize } = useColumnResize(tableRef, setColumnWidth);

  const handleFitColumnToContent = useCallback(
    (field: string) => {
      setColumnWidth(field, null);
      refitColumn(field);
    },
    [refitColumn, setColumnWidth],
  );

  // A reset from any table with this id refits this one too.
  useEffect(() => subscribeToSharedLayoutResets(id, resetFloors), [id, resetFloors]);

  const handleToggleDetailPanel = (rowId: string) => {
    const nextExpandedRowIds = expandedRowIdSet.has(rowId)
      ? expandedRowIds.filter((expandedId) => expandedId !== rowId)
      : [...expandedRowIds, rowId];

    const isUncontrolled = controlledExpandedRowIds === undefined;

    if (isUncontrolled) {
      setUncontrolledExpandedRowIds(nextExpandedRowIds);
    }

    onExpandedRowIdsChange?.(nextExpandedRowIds);
  };

  const selectedRowIdSet = useMemo(() => new Set(selectedRowIds), [selectedRowIds]);
  const selectedRowsOnPageCount = rows.filter((row) =>
    selectedRowIdSet.has(row.id),
  ).length;
  const allRowsOnPageSelected =
    rows.length > 0 && selectedRowsOnPageCount === rows.length;
  const someRowsOnPageSelected = selectedRowsOnPageCount > 0 && !allRowsOnPageSelected;

  const handleToggleRowSelected = (rowId: string) => {
    const nextSelection = selectedRowIdSet.has(rowId)
      ? (selectedRowIds ?? []).filter((selectedId) => selectedId !== rowId)
      : [...(selectedRowIds ?? []), rowId];

    onSelectedRowIdsChange?.(nextSelection);
  };

  // Select-all covers the rows on this page and leaves selections from other pages
  // alone.
  const handleToggleAllRowsOnPageSelected = () => {
    const rowIdsOnPage = new Set(rows.map((row) => row.id));
    const selectionOffPage = (selectedRowIds ?? []).filter(
      (selectedId) => !rowIdsOnPage.has(selectedId),
    );

    onSelectedRowIdsChange?.(
      allRowsOnPageSelected ? selectionOffPage : [...selectionOffPage, ...rowIdsOnPage],
    );
  };

  const { center: centerSlots, end: endSlots, start: startSlots } = visibleColumnSlots;
  const hasRows = rows.length > 0;
  const shouldShowNoRows = !hasRows && !loading;
  const leadingColumnCount = Number(hasDetailPanel) + Number(hasRowSelection);
  // The filler cell between the centre and right-pinned columns counts as one.
  const totalColumnCount =
    leadingColumnCount + startSlots.length + centerSlots.length + 1 + endSlots.length;

  const pinnedEdges: PinnedEdges = {
    borderColor: tokens.borderColor,
    firstEndField: endSlots[0]?.column.field,
    lastStartField: startSlots.at(-1)?.column.field,
  };

  const headerBackground = { backgroundColor: tokens.headerBackground };

  const leadingCellSx = (
    background: CellBackground,
    width: number,
    accentColor?: string,
  ): SxProps<Theme> => ({
    ...stickyCellSx(background),
    boxShadow: getAccentShadow(accentColor),
    // Automatic layout treats a cell's width as a preference and squeezes it to its
    // content when space runs out, which would slide the next sticky cell over it.
    minWidth: width,
    p: 0,
    textAlign: 'center',
    width,
  });

  const renderLeadingHeaderCells = () => (
    <>
      {hasDetailPanel && (
        <TableCell
          sx={leadingCellSx(headerBackground, DETAIL_TOGGLE_COLUMN_WIDTH_PX)}
          style={{ left: 0 }}
        />
      )}
      {hasRowSelection && (
        <TableCell
          sx={leadingCellSx(headerBackground, SELECTION_COLUMN_WIDTH_PX)}
          style={{ left: hasDetailPanel ? DETAIL_TOGGLE_COLUMN_WIDTH_PX : 0 }}
        >
          <Checkbox
            checked={allRowsOnPageSelected}
            disabled={!hasRows || loading}
            indeterminate={someRowsOnPageSelected}
            onChange={handleToggleAllRowsOnPageSelected}
            size="small"
            slotProps={{ input: { 'aria-label': t('common.select_all') } }}
          />
        </TableCell>
      )}
    </>
  );

  const getColors = (row: TRow) =>
    getRowColors?.(row, expandedRowIdSet.has(row.id)) ?? {};

  const getTintImage = ({ tint }: DataTableRowColors) =>
    tint ? `linear-gradient(${tint}, ${tint})` : undefined;

  const hoverImage = `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`;

  const renderLeadingBodyCells = (
    row: TRow,
    isExpanded: boolean,
    background: CellBackground,
    accentColor?: string,
  ) => (
    <>
      {hasDetailPanel && (
        <TableCell
          onClick={stopRowClick}
          sx={leadingCellSx(background, DETAIL_TOGGLE_COLUMN_WIDTH_PX, accentColor)}
          style={{ left: 0 }}
        >
          <IconButton
            aria-expanded={isExpanded}
            aria-label={
              isExpanded ? t('actions.hide_details') : t('actions.show_details')
            }
            onClick={() => handleToggleDetailPanel(row.id)}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            {isExpanded ? (
              <KeyboardArrowDown sx={{ fontSize: 18 }} />
            ) : (
              <KeyboardArrowRight sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </TableCell>
      )}
      {hasRowSelection && (
        <TableCell
          onClick={stopRowClick}
          sx={leadingCellSx(
            background,
            SELECTION_COLUMN_WIDTH_PX,
            hasDetailPanel ? undefined : accentColor,
          )}
          style={{ left: hasDetailPanel ? DETAIL_TOGGLE_COLUMN_WIDTH_PX : 0 }}
        >
          <Checkbox
            checked={selectedRowIdSet.has(row.id)}
            onChange={() => handleToggleRowSelected(row.id)}
            size="small"
            slotProps={{ input: { 'aria-label': t('data_table.select_row') } }}
          />
        </TableCell>
      )}
    </>
  );

  const renderHeaderCell = (slot: ColumnSlot<TRow>) => {
    const { column } = slot;

    return (
      <TableCell
        key={column.field}
        component="th"
        aria-label={column.isHeaderNameHidden ? column.headerName : undefined}
        data-field={column.field}
        scope="col"
        sx={{
          ...cellPaddingSx,
          ...slot.cellSx,
          backgroundColor: tokens.headerBackground,
          fontSize,
          fontWeight: 600,
          height: headerHeightPx,
          position: 'relative',
          ...getPinnedSx(column, headerBackground, pinnedEdges),
        }}
        style={getPinnedOffsetStyle(column, pinnedOffsets, leadingColumnsWidth)}
      >
        <Tooltip title={column.description}>
          <Box sx={slot.contentSx}>
            <span data-floor-field={column.field}>
              {column.isHeaderNameHidden ? null : column.headerName}
            </span>
          </Box>
        </Tooltip>
        {slot.isResizable ? (
          <Box
            aria-label={t('data_table.resize_column', { column: column.headerName })}
            aria-orientation="vertical"
            onDoubleClick={() => handleFitColumnToContent(column.field)}
            onPointerDown={(event) =>
              startResize(event, column.field, slot.minWidth, column.maxWidth)
            }
            role="separator"
            sx={{
              cursor: 'col-resize',
              height: '100%',
              position: 'absolute',
              right: 0,
              top: 0,
              touchAction: 'none',
              userSelect: 'none',
              width: RESIZE_HANDLE_WIDTH_PX,
              ...separatorLineSx(tokens),
              '&:hover, &[data-resizing="true"]': {
                borderRight: 2,
                borderRightColor: tokens.resizeHandleActiveColor,
              },
            }}
          />
        ) : (
          <Box
            aria-hidden
            sx={{
              height: '100%',
              position: 'absolute',
              right: 0,
              top: 0,
              ...separatorLineSx(tokens),
            }}
          />
        )}
      </TableCell>
    );
  };

  const renderBodyCell = (
    slot: ColumnSlot<TRow>,
    row: TRow,
    background: CellBackground,
  ) => {
    const { column } = slot;
    const { content, plainText } = getCellContent(column, row);

    return (
      <TableCell
        key={column.field}
        sx={{
          ...cellPaddingSx,
          ...slot.cellSx,
          fontSize,
          height: rowHeightPx,
          ...getPinnedSx(column, background, pinnedEdges),
        }}
        style={getPinnedOffsetStyle(column, pinnedOffsets, leadingColumnsWidth)}
      >
        {/* Rows keep the previous grid's fixed height: a cell taller than the row is clipped
            rather than stretching the whole row. The 1px is the row's bottom border. */}
        <DataTableCellContent
          fullText={plainText}
          sx={{ ...slot.contentSx, maxHeight: rowHeightPx - 1 }}
        >
          <Box
            component="span"
            data-floor-field={column.field}
            sx={column.renderCell ? customCellContentSx : undefined}
          >
            {content}
          </Box>
        </DataTableCellContent>
      </TableCell>
    );
  };

  const getBodyRowProps = (row: TRow): BodyRowProps => {
    const colors = getColors(row);

    return {
      hover: !colors.surface,
      onClick: onRowClick ? () => onRowClick(row) : undefined,
      sx: {
        backgroundColor: colors.surface,
        backgroundImage: getTintImage(colors),
        cursor: onRowClick ? 'pointer' : undefined,
      },
      'data-id': row.id,
      'data-test-id': 'data-table-row',
    };
  };

  const renderBodyCells = (row: TRow) => {
    const colors = getColors(row);
    const tintImage = getTintImage(colors);
    const rowBackground = {
      backgroundColor: colors.surface ?? tokens.bodyBackground,
      backgroundImage: tintImage,
      hoverBackgroundImage: tintImage ? `${tintImage}, ${hoverImage}` : hoverImage,
    };

    return (
      <>
        {renderLeadingBodyCells(
          row,
          expandedRowIdSet.has(row.id),
          rowBackground,
          colors.accent,
        )}
        {startSlots.map((slot) => renderBodyCell(slot, row, rowBackground))}
        {centerSlots.map((slot) => renderBodyCell(slot, row, rowBackground))}
        <TableCell sx={{ p: 0 }} />
        {endSlots.map((slot) => renderBodyCell(slot, row, rowBackground))}
      </>
    );
  };

  const renderDetailPanelCell = (row: TRow) => (
    <TableCell colSpan={totalColumnCount} sx={{ p: 0 }}>
      {/* A spanning cell's content counts towards column widths under automatic
          layout; this stops a wide panel stretching the columns above it. */}
      <Box
        sx={{
          ...fullWidthContentSx,
          boxShadow: getAccentShadow(getColors(row).accent),
          contain: 'inline-size',
        }}
      >
        {renderDetailPanel?.(row)}
      </Box>
    </TableCell>
  );

  const renderFooterCell = () => (
    <TableCell colSpan={totalColumnCount} sx={{ borderBottom: 0, p: 0 }}>
      <Box sx={fullWidthContentSx}>{footer}</Box>
    </TableCell>
  );

  const hasFooter = Boolean(footer);
  const virtualizedItems = useMemo(
    () => [
      ...rows.flatMap((row): Array<VirtualizedItem<TRow>> =>
        hasDetailPanel && expandedRowIdSet.has(row.id)
          ? [
              { kind: 'row', row },
              { kind: 'detail', row },
            ]
          : [{ kind: 'row', row }],
      ),
      ...(hasFooter ? [{ kind: 'footer' } as const] : []),
    ],
    [expandedRowIdSet, hasDetailPanel, hasFooter, rows],
  );

  // Only rows not yet measured grow the floors, so mounting detail panels (expand all)
  // doesn't re-measure the whole table.
  const measuredRowIdsRef = useRef(new Set<string>());
  const growFloorsFrameRef = useRef(0);
  useLayoutEffect(() => {
    measuredRowIdsRef.current = new Set();
  }, [rows]);
  useEffect(() => () => cancelAnimationFrame(growFloorsFrameRef.current), []);

  const handleItemsRendered = (renderedItems: ListItem<unknown>[]) => {
    const unmeasuredRowIds = renderedItems.flatMap(({ index }) => {
      const item = virtualizedItems[index];

      return item?.kind === 'row' && !measuredRowIdsRef.current.has(item.row.id)
        ? [item.row.id]
        : [];
    });

    if (unmeasuredRowIds.length === 0) {
      return;
    }

    unmeasuredRowIds.forEach((rowId) => measuredRowIdsRef.current.add(rowId));
    cancelAnimationFrame(growFloorsFrameRef.current);
    // New rows are only in the DOM by the next frame.
    growFloorsFrameRef.current = requestAnimationFrame(growFloors);
  };

  const [scrollerElement, setScrollerElement] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (!scrollerElement) {
      return;
    }

    // A CSS variable rather than state, so resizing re-renders nothing.
    const measureVisibleWidth = () =>
      scrollerElement.style.setProperty(
        VISIBLE_WIDTH_VARIABLE,
        `${scrollerElement.clientWidth}px`,
      );
    measureVisibleWidth();
    const resizeObserver = new ResizeObserver(measureVisibleWidth);
    resizeObserver.observe(scrollerElement);

    return () => resizeObserver.disconnect();
  }, [scrollerElement]);

  const tableProps: ComponentPropsWithRef<typeof Table> = {
    'aria-busy': loading,
    ref: tableRef,
    style: draggedWidthVariables,
    sx: {
      // Collapsed borders straddle the table edge by half a pixel.
      borderCollapse: 'separate',
      borderSpacing: 0,
      tableLayout: 'auto',
      width: '100%',
      '& .MuiTableBody-root .MuiTableCell-root': {
        borderBottomColor: tokens.borderColor,
      },
      '& .MuiTableHead-root .MuiTableCell-root': {
        borderBottomColor: tokens.separatorColor,
      },
      // Doubled class so it outranks the theme's `&.MuiChip-sizeMedium` height.
      ...(chipHeightPx
        ? { '& .MuiChip-root.MuiChip-root': { fontSize, height: chipHeightPx } }
        : {}),
    },
  };
  const bodySx = { opacity: loading ? LOADING_BODY_OPACITY : 1 };

  const headerRow = (
    <TableRow>
      {renderLeadingHeaderCells()}
      {startSlots.map(renderHeaderCell)}
      {centerSlots.map(renderHeaderCell)}
      {/* Has no content, so it only takes width when no other column can: every column
          dragged to a width or pinned. That keeps right-pinned columns at the table's
          right edge. */}
      <TableCell sx={{ backgroundColor: tokens.headerBackground, p: 0 }} />
      {endSlots.map(renderHeaderCell)}
    </TableRow>
  );

  const renderBodyRow = (row: TRow, props: TableRowProps) => {
    const rowProps = { ...getBodyRowProps(row), ...props };

    return RowComponent ? (
      <RowComponent {...rowProps} row={row} />
    ) : (
      <TableRow {...rowProps} />
    );
  };

  const renderVirtualizedRow = (index: number, props: TableRowProps) => {
    const item = virtualizedItems[index];

    if (item.kind === 'row') {
      return renderBodyRow(item.row, props);
    }

    if (item.kind === 'detail') {
      return (
        <TableRow
          {...props}
          sx={{
            backgroundColor: getColors(item.row).surface,
          }}
        />
      );
    }

    return <TableRow {...props} />;
  };

  return (
    <>
      <Box
        sx={[
          {
            backgroundColor: tokens.containerBackground,
            border: 1,
            borderColor: tokens.borderColor,
            borderRadius: `${tokens.borderRadius}px`,
            boxShadow: tokens.containerShadow,
            pb: 2,
            pt: hideToolbar ? 0 : 2,
            ...(isVirtualized ? { display: 'flex', flexDirection: 'column' } : {}),
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {!hideToolbar && (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              justifyContent: 'space-between',
              mb: 2,
              pt: 0.5,
              px: 2,
            }}
          >
            {/* The previous grid's toolbar kept an empty 16px-margin slot before its Columns
              button, and nudged trailing content up 2px to centre it. */}
            <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
              {columnsMenuContainer === undefined && (
                <DataTableColumnChooser
                  columns={chooserColumns}
                  onMoveColumn={moveColumn}
                  onReset={reset}
                  onToggleColumn={toggleColumn}
                />
              )}
              {headerLeadingComponent}
            </Box>
            <Box sx={{ alignItems: 'center', display: 'flex' }}>
              {onSearchChange && (
                <DataTableSearchField loading={loading} onSearchChange={onSearchChange} />
              )}
              <Box sx={{ pb: '2px' }}>{headerTrailingComponent}</Box>
            </Box>
          </Box>
        )}
        <Box
          sx={{
            borderBottom: 1,
            borderBottomColor: tokens.borderColor,
            ...(isVirtualized ? { flex: 1, minHeight: 0, position: 'relative' } : {}),
          }}
        >
          {/* `contain` stops the table's width from widening this box, and through it
              grid or flex parents that size to their content. */}
          <Box
            data-test-id="data-table-scroller"
            ref={isVirtualized ? undefined : setScrollerElement}
            sx={{
              contain: 'inline-size',
              position: 'relative',
              ...(isVirtualized ? { height: '100%' } : { overflowX: 'auto' }),
              ...(loading && !hasRows
                ? { minHeight: headerHeightPx + NO_ROWS_AREA_HEIGHT_PX }
                : {}),
            }}
          >
            {isVirtualized ? (
              <TableVirtuoso
                components={virtualizedTableComponents}
                computeItemKey={(index) => {
                  const item = virtualizedItems[index];

                  return item.kind === 'footer'
                    ? item.kind
                    : `${item.kind}-${item.row.id}`;
                }}
                context={{
                  bodySx,
                  renderRow: renderVirtualizedRow,
                  tableProps,
                }}
                defaultItemHeight={rowHeightPx}
                endReached={() => onEndReached?.()}
                fixedHeaderContent={() => headerRow}
                increaseViewportBy={rowHeightPx * OVERSCAN_ROW_COUNT}
                itemContent={(index) => {
                  const item = virtualizedItems[index];

                  if (item.kind === 'row') {
                    return renderBodyCells(item.row);
                  }

                  return item.kind === 'detail'
                    ? renderDetailPanelCell(item.row)
                    : renderFooterCell();
                }}
                itemsRendered={handleItemsRendered}
                scrollerRef={(element) =>
                  setScrollerElement(element instanceof HTMLElement ? element : null)
                }
                style={{ height: '100%' }}
                totalCount={virtualizedItems.length}
              />
            ) : (
              <Table {...tableProps}>
                <TableHead>{headerRow}</TableHead>
                <TableBody sx={bodySx}>
                  {rows.map((row) => (
                    <Fragment key={row.id}>
                      {renderBodyRow(row, { children: renderBodyCells(row) })}
                      {expandedRowIdSet.has(row.id) && renderDetailPanel && (
                        <TableRow sx={{ backgroundColor: getColors(row).surface }}>
                          {renderDetailPanelCell(row)}
                        </TableRow>
                      )}
                    </Fragment>
                  ))}
                  {hasFooter && <TableRow>{renderFooterCell()}</TableRow>}
                </TableBody>
              </Table>
            )}
            {loading && (
              <LinearProgress
                sx={{ left: 0, position: 'absolute', right: 0, top: headerHeightPx }}
              />
            )}
          </Box>
          {shouldShowNoRows && (
            <Box
              data-test-id="data-table-no-rows"
              sx={{
                minHeight: NO_ROWS_AREA_HEIGHT_PX,
                ...(isVirtualized
                  ? { left: 0, position: 'absolute', right: 0, top: headerHeightPx }
                  : {}),
              }}
            >
              <NoDataFound action={noRowsOverlayAction} noDataText={noRowsOverlayText} />
            </Box>
          )}
        </Box>
      </Box>
      {columnsMenuContainer &&
        createPortal(
          <DataTableColumnChooser
            columns={chooserColumns}
            isIconTrigger
            onMoveColumn={moveColumn}
            onReset={reset}
            onToggleColumn={toggleColumn}
          />,
          columnsMenuContainer,
        )}
      {selectedRowIds && (
        <DataGridBottomBar
          selectedRowIDs={selectedRowIds}
          onClearSelectedRows={() => onSelectedRowIdsChange?.([])}
        />
      )}
    </>
  );
}

export default function DataTable<TRow extends DataTableRow>(
  props: DataTableProps<TRow>,
) {
  const renderMode = props.isVirtualized ? 'virtualized' : 'table';

  return <DataTableContent key={`${props.id}-${renderMode}`} {...props} />;
}
